import PropTypes from 'prop-types';

import {
  Chip,
  Stack,
  Alert,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import { ListingTextLine, ListingThumbnail } from 'src/components/listing-row/listing-row';

import { SEVERITY_ICONS } from './utils/bulk-results-copy';
import { listingsWord } from './utils/listing-count-words';


// How each outcome reads on screen. 'skipped' is deliberately not an error: nothing went wrong with
// the item, it is simply still for sale, and what the seller does about that is take it off its
// marketplace — not try again.
const OUTCOMES = {
  success: { label: 'Archived', color: 'success', icon: 'solar:check-circle-bold' },
  skipped: { label: 'Still for sale', color: 'warning', icon: 'solar:danger-triangle-bold' },
  failed: { label: 'Not archived', color: 'error', icon: 'solar:close-circle-bold' },
};

/**
 * What the run did, in one sentence.
 *
 * Items still for sale are counted apart from failures, because they are the only ones the seller
 * has something to do about.
 */
function summarise({ total, success, skipped = 0, failed }) {
  if (success === total) {
    return { severity: 'success', message: `${listingsWord(success)} archived` };
  }
  if (failed === total) {
    return { severity: 'error', message: `Couldn't archive ${listingsWord(failed)}. Try again.` };
  }

  const parts = [];
  if (success) parts.push(`${listingsWord(success)} archived`);
  if (skipped) parts.push(`${listingsWord(skipped)} still for sale`);
  if (failed) parts.push(`${listingsWord(failed)} couldn't be archived`);
  return { severity: 'warning', message: `${parts.join(', ')}.` };
}

/**
 * The report a seller reads after a bulk archive.
 *
 * Every item says what happened to it in plain words on its own row — no expanding, no chevrons.
 * The reason an item was left alone is the whole point of the panel, so hiding it behind a control
 * is hiding the only thing worth reading.
 */
export default function BulkArchiveResultsDialog({ open, onClose, results }) {
  if (!results) return null;

  const { details } = results;
  const summary = summarise(results);

  // Items archived here but still up on an account Voolist can't reach — a double-sale exposure
  // only the seller can close.
  const stillLive = [
    ...new Set((details || []).flatMap((d) => d.accountsStillLive || [])),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify
            icon={SEVERITY_ICONS[summary.severity]}
            width={24}
            sx={{ color: `${summary.severity}.main` }}
          />
          <span>Archive results</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity={summary.severity}>{summary.message}</Alert>

          {stillLive.length > 0 && (
            <Alert severity="warning">
              Still for sale on <strong>{stillLive.join(', ')}</strong>, which Voolist can&apos;t
              reach. Reconnect {stillLive.length === 1 ? 'that account' : 'those accounts'} on the
              Marketplaces page, or take those listings off there yourself, so you don&apos;t sell
              them twice.
            </Alert>
          )}

          <Stack spacing={1}>
            {details?.map((detail) => {
              const outcome = OUTCOMES[detail.status] || OUTCOMES.failed;

              return (
                <Stack
                  key={detail.listingId}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <ListingThumbnail src={detail.thumbnail} alt={detail.title} size={40} rounded={1} />

                  <ListingTextLine
                    listing={{ title: detail.title }}
                    secondLine={
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {detail.message}
                      </Typography>
                    }
                  />

                  <Chip
                    icon={<Iconify icon={outcome.icon} width={16} />}
                    label={outcome.label}
                    color={outcome.color}
                    size="small"
                    sx={{ flexShrink: 0, minWidth: 110 }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkArchiveResultsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    skipped: PropTypes.number,
    failed: PropTypes.number,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        thumbnail: PropTypes.string,
        status: PropTypes.oneOf(['success', 'skipped', 'failed']),
        message: PropTypes.string,
        // The accounts keeping a left-alone item out of the Archive...
        accounts: PropTypes.array,
        // ...and the ones an archived item stays up on because Voolist can't reach them.
        accountsStillLive: PropTypes.array,
      })
    ),
  }),
};
